import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ImpactSection = styled.section`
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
  position: relative;
  overflow: hidden;
  background: ${props => props.theme.colors.backgroundSecondary};
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xl};
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

const TextContent = styled.div``;

const SectionLabel = styled(motion.span)`
  display: inline-block;
  padding: 8px 16px;
  background: ${props => props.theme.gradients.subtle};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: ${props => props.theme.typography.weights.medium};
  color: ${props => props.theme.colors.accentPurple};
  margin-bottom: ${props => props.theme.spacing.md};
  letter-spacing: 0.02em;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  margin-bottom: ${props => props.theme.spacing.md};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

const Description = styled(motion.p)`
  font-size: 1rem;
  color: ${props => props.theme.colors.subtextGray};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const HighlightText = styled.span`
  color: ${props => props.theme.colors.accentPurple};
  font-weight: ${props => props.theme.typography.weights.semibold};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 10px;
  }
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 16px;
  padding: ${props => props.theme.spacing.md};
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accentPurple};
    transform: translateY(-2px);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm};
  }
`;

const StatValue = styled.div`
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: ${props => props.theme.typography.weights.extrabold};
  background: ${props => props.theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 2px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.subtextGray};
  font-weight: ${props => props.theme.typography.weights.medium};
`;

const Impact = () => {
  const stats = [
    { value: '1', label: 'Connected Workspace' },
    { value: '4', label: 'AI-Powered Tools' },
    { value: '8', label: 'Cert Modules' },
    { value: '12+', label: 'Tools at Launch' },
  ];

  return (
    <ImpactSection>
      <Container>
        <ContentWrapper>
          <TextContent>
            <SectionLabel
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Why a Connected Platform
            </SectionLabel>
            <SectionTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              One Source of Truth for Improvement Work
            </SectionTitle>
            <Description
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Fragmented tools mean fragmented progress. Continua Labs brings learning, planning, execution, and improvement into{' '}
              <HighlightText>one workspace your whole team shares</HighlightText>. Credits, leaderboards, and team activity tie it all together.
            </Description>
          </TextContent>

          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
              >
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </ContentWrapper>
      </Container>
    </ImpactSection>
  );
};

export default Impact;
